import React, {useCallback, useEffect, useMemo} from 'react';
import ReactDOM from 'react-dom';
import {FissionV4Open, FissionV4Progress} from '../../fission-v4.service';
import {CashEverydayModal} from './components/modals';
import {waitForCashEverydayImages} from './assets';
import {useCashGiftSelection} from './hooks/use-cash-gift-selection';
import {useRewardModals} from './hooks/use-reward-modals';
import {WITHDRAW_TARGET_AMOUNT} from './constants';
import {normalizeFissionTargetAmount} from './utils/runtime-config';
import ReferEarnWheelStyles from './styles';

interface EntryCashGiftModalProps {
  progress: FissionV4Progress;
  onReadyToNavigate: (nextRound: FissionV4Open) => void | Promise<void>;
  onOpened: (nextRound: FissionV4Open) => void;
}

function EntryCashGiftModal({
  progress,
  onReadyToNavigate,
  onOpened,
}: EntryCashGiftModalProps) {
  const {
    rewardModalVisible,
    closingRewardModals,
    openRewardModal,
    closeRewardModal,
  } = useRewardModals();
  const cashGiftAwardValues = useMemo(() => {
    const values =
      progress.boxNumArr ||
      (progress.frontAwardItems?.length === 4 ? progress.frontAwardItems : []);

    return values
      .slice(0, 4)
      .map(value => Number(value))
      .filter(value => Number.isFinite(value));
  }, [progress.boxNumArr, progress.frontAwardItems]);
  const targetAmount =
    normalizeFissionTargetAmount(progress.targetAmount) ??
    WITHDRAW_TARGET_AMOUNT;
  const closeCashGiftModal = useCallback(
    (onClosed?: () => void) => closeRewardModal('cash', onClosed),
    [closeRewardModal],
  );
  const handleOpened = useCallback(
    (nextRound?: FissionV4Open) => onOpened(nextRound || {}),
    [onOpened],
  );
  const handleBeforeClose = useCallback(
    async (nextRound: FissionV4Open) => {
      await onReadyToNavigate(nextRound);
    },
    [onReadyToNavigate],
  );
  const {
    selectedCashGiftIndex,
    cashGiftRevealPhase,
    cashGiftRevealItems,
    showCashGiftIdleEffects,
    cashGiftGuideRef,
    showCashGiftGuide,
    cashGiftGuideDismissing,
    chooseCashGift,
  } = useCashGiftSelection({
    visible: rewardModalVisible.cash,
    cashGiftAwardValues,
    onOpened: handleOpened,
    onCloseCash: closeCashGiftModal,
    onBeforeClose: handleBeforeClose,
  });

  useEffect(() => {
    openRewardModal('cash');
  }, [openRewardModal]);

  return (
    <>
      <ReferEarnWheelStyles />
      <CashEverydayModal
        visible={rewardModalVisible.cash}
        closing={Boolean(closingRewardModals.cash)}
        targetAmount={targetAmount}
        selectedGiftIndex={selectedCashGiftIndex}
        cashGiftRevealPhase={cashGiftRevealPhase}
        cashGiftRevealItems={cashGiftRevealItems}
        showCashGiftIdleEffects={showCashGiftIdleEffects}
        cashGiftGuideRef={cashGiftGuideRef}
        showCashGiftGuide={showCashGiftGuide}
        cashGiftGuideDismissing={cashGiftGuideDismissing}
        onChooseGift={chooseCashGift}
      />
    </>
  );
}

export async function openReferEarnEntryCashGift(
  progress: FissionV4Progress,
  onReadyToNavigate: (nextRound: FissionV4Open) => void | Promise<void>,
) {
  await waitForCashEverydayImages();

  return new Promise<FissionV4Open>(resolve => {
    const container = document.createElement('div');
    container.dataset.referEarnEntryCashGift = 'true';
    document.body.appendChild(container);

    const handleOpened = (nextRound: FissionV4Open) => {
      ReactDOM.unmountComponentAtNode(container);
      container.remove();
      resolve(nextRound);
    };

    ReactDOM.render(
      <EntryCashGiftModal
        progress={progress}
        onReadyToNavigate={onReadyToNavigate}
        onOpened={handleOpened}
      />,
      container,
    );
  });
}
